// SPDX-License-Identifier: AGPL-3.0-or-later

import {getCosmeticAssetURL} from '@app/features/cosmetics/utils/CosmeticAssetUtils';
import {isRoomPartType, roomPartPreviewUrl} from '@app/features/cosmetics/utils/RoomOutfitModels';
import {useNearViewport} from '@app/features/messaging/hooks/useNearViewport';
import {ShopMemberRowPreview} from '@app/features/shop/components/previews/ShopMemberRowPreview';
import styles from '@app/features/shop/components/ShopItemStage.module.css';
import {previewEffectFor, previewUserFor} from '@app/features/shop/utils/ShopPreviewUser';
import {shopStageLayout} from '@app/features/shop/utils/ShopStageLayout';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Avatar} from '@app/features/ui/components/Avatar';
import {ProfilePreview} from '@app/features/user/components/profile/ProfilePreview';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import type {CosmeticItemResponse} from '@fluxer/schema/src/domains/cosmetics/CosmeticResponseSchemas';
import {observer} from 'mobx-react-lite';
import type {CSSProperties} from 'react';

// A room character or emote is shown by its own image: no app surface outside 3D voice rooms draws it. A part's image
// is the manifest preview (the part on the default body); a card never starts a 3D scene.
const CHARACTER_IMAGE_STYLE: CSSProperties = {
	position: 'absolute',
	inset: 0,
	width: '100%',
	height: '100%',
	objectFit: 'contain',
};
const CHARACTER_IMAGE_CSS_SIZE = 240;

function CharacterImage({item}: {item: CosmeticItemResponse}) {
	const src = isRoomPartType(item.type)
		? roomPartPreviewUrl(item.model_key)
		: getCosmeticAssetURL(item.type, {asset: item.asset, item_id: item.id}, CHARACTER_IMAGE_CSS_SIZE, false);
	if (!src) return null;
	return (
		<img
			style={CHARACTER_IMAGE_STYLE}
			src={src}
			alt=""
			draggable={false}
			data-flx="shop.shop-item-stage.character-image"
		/>
	);
}

interface ShopItemStageProps {
	// One item, or a bundle's items worn together.
	items: ReadonlyArray<CosmeticItemResponse>;
	size: 'grid' | 'full';
	// Whether stage cosmetics play: a grid card passes its hover, the dialogs leave them on.
	animateNameplate?: boolean;
	// A bundle's shop card: every item at once instead of one view (grid only).
	composed?: boolean;
	// A set's own showcase (its tile and its page's bundle card): a composed bundle drawn half again as large.
	showcase?: boolean;
}

// The stage's layout: wings anywhere in a bundle need the ultra band, only nameplates the member row.
function stageType(items: ReadonlyArray<CosmeticItemResponse>): string | undefined {
	if (items.some((item) => item.type === 'avatar_ultra')) return 'avatar_ultra';
	if (items.every((item) => item.type === 'nameplate')) return 'nameplate';
	return items[0]?.type;
}

function isRoomItemType(type: string): boolean {
	return type === 'room_character' || type === 'room_emote' || isRoomPartType(type);
}

// Grid avatar products use one real avatar; effects use the profile and nameplates the member row. Full previews
// use the real profile card. Grid content mounts near the viewport and stays inert; cosmetics follow card hover.
export const ShopItemStage = observer(function ShopItemStage({
	items,
	size,
	animateNameplate = true,
	composed = false,
	showcase = false,
}: ShopItemStageProps) {
	const grid = size === 'grid';
	const composition = grid && composed;
	const {ref, isNearViewport} = useNearViewport<HTMLDivElement>({disabled: !grid});
	const user = Users.currentUser;
	const previewUser = user && isNearViewport ? previewUserFor(user, ...items) : null;
	const type = stageType(items);
	const character = !composition && type && isRoomItemType(type) ? items[0] : undefined;
	const avatarOnly =
		grid && !composition && items.some((item) => item.type === 'avatar_ultra' || item.type === 'avatar_decoration');
	const layout =
		previewUser && (avatarOnly || (!grid && type !== 'nameplate')) ? shopStageLayout(previewUser, size) : undefined;
	return (
		<div
			ref={ref}
			className={styles.stage}
			data-type={type}
			data-size={size}
			data-composed={composition || undefined}
			data-showcase={(composition && showcase) || undefined}
			data-ultra={(composition && items.some((item) => item.type === 'avatar_ultra')) || undefined}
			data-custom-layout={layout ? true : undefined}
			style={
				layout
					? ({
							'--natural-w': remFromPx(layout.width),
							'--natural-h': layout.height === undefined ? undefined : remFromPx(layout.height),
							'--card-x': remFromPx(layout.cardX),
							'--card-y': remFromPx(layout.cardY),
							'--stage-scale': layout.scale,
						} as CSSProperties)
					: undefined
			}
			data-flx="shop.shop-item-stage.stage"
		>
			{character && isNearViewport && (
				<CharacterImage item={character} data-flx="shop.shop-item-stage.character-image" />
			)}
			{!character && previewUser && (
				<div
					className={styles.natural}
					inert={grid}
					aria-hidden={grid || undefined}
					data-flx="shop.shop-item-stage.natural"
				>
					{composition ? (
						<BundleComposition
							items={items}
							user={user ?? previewUser}
							animate={animateNameplate}
							ultraSize={(showcase ? ULTRA_SHOWCASE_SIZE : ULTRA_CHAT_SIZE) * BUNDLE_ULTRA_GROWTH}
							data-flx="shop.shop-item-stage.bundle-composition"
						/>
					) : avatarOnly ? (
						<div className={styles.card} data-flx="shop.shop-item-stage.card">
							<Avatar
								user={previewUser}
								size={80}
								decorationSurface="profile"
								forceAnimate={animateNameplate || type === 'avatar_ultra'}
								data-flx="shop.shop-item-stage.avatar"
							/>
						</div>
					) : type === 'nameplate' ? (
						<div className={styles.memberColumn} data-flx="shop.shop-item-stage.member-column">
							<SkeletonRow data-flx="shop.shop-item-stage.skeleton-row" />
							<ShopMemberRowPreview
								user={previewUser}
								animateNameplate={animateNameplate}
								data-flx="shop.shop-item-stage.shop-member-row-preview"
							/>
							<SkeletonRow data-flx="shop.shop-item-stage.skeleton-row--2" />
						</div>
					) : (
						<div className={styles.card} data-flx="shop.shop-item-stage.card">
							<ProfilePreview
								user={previewUser}
								previewProfileEffect={previewEffectFor(...items)}
								showMembershipInfo={false}
								showMessageButton={false}
								showPreviewLabel={false}
								data-flx="shop.shop-item-stage.profile-preview"
							/>
						</div>
					)}
					{!grid && type !== 'nameplate' && items.some((item) => item.type === 'nameplate') && (
						<div className={styles.plateRow} data-flx="shop.shop-item-stage.plate-row">
							<ShopMemberRowPreview
								user={previewUser}
								animateNameplate={animateNameplate}
								data-flx="shop.shop-item-stage.shop-member-row-preview--2"
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
});

interface BundleCompositionProps {
	items: ReadonlyArray<CosmeticItemResponse>;
	user: User;
	animate: boolean;
	ultraSize: number;
}

// The avatar size of a chat message, where ultra wings have their natural size.
const ULTRA_CHAT_SIZE = 40;
// A set's showcase draws the wings half again as large from a larger avatar, never by scaling: the image is fetched
// at that size and the in-front layer, sized from the avatar, stays over it.
const ULTRA_SHOWCASE_SIZE = ULTRA_CHAT_SIZE * 1.5;
// On a bundle card the wings are hard to make out at chat size, so they come from an avatar 30% larger, again
// fetched at that size rather than scaled.
const BUNDLE_ULTRA_GROWTH = 1.3;

// Slot order on a bundle card: avatar items, then nameplates, then profile effects.
const COMPOSITION_RANK: Record<CosmeticItemResponse['type'], number> = {
	avatar_ultra: 0,
	avatar_decoration: 0,
	nameplate: 1,
	profile_effect: 2,
	room_character: 3,
	room_face: 3,
	room_hair: 3,
	room_top: 3,
	room_bottom: 3,
	room_shoes: 3,
	room_costume: 3,
	room_hat: 3,
	room_glasses: 3,
	room_accessory: 3,
	room_emote: 3,
};

// Every item of a bundle at once, each worn alone in its own slot and in the app's own preview: an avatar item on the
// avatar, a nameplate in a member row, an effect on the profile card. Two items of one type get two slots. The fewer
// the slots, the larger each part is drawn.
function BundleComposition({items, user, animate, ultraSize}: BundleCompositionProps) {
	const ordered = [...items].sort((a, b) => COMPOSITION_RANK[a.type] - COMPOSITION_RANK[b.type]);
	const ultra = items.some((item) => item.type === 'avatar_ultra');
	return (
		<div
			className={styles.composition}
			data-parts={ordered.length}
			data-ultra={ultra || undefined}
			style={ultra ? ({'--ultra-avatar-size': remFromPx(ultraSize)} as CSSProperties) : undefined}
			data-flx="shop.shop-item-stage.composition"
		>
			{ordered.map((item) => (
				<div
					key={item.id}
					className={styles.compositionSlot}
					data-part={isRoomItemType(item.type) ? undefined : item.type}
					data-flx="shop.shop-item-stage.composition-slot"
				>
					<CompositionPart
						item={item}
						user={user}
						animate={animate}
						ultraSize={ultraSize}
						data-flx="shop.shop-item-stage.bundle-composition.composition-part"
					/>
				</div>
			))}
		</div>
	);
}

function CompositionPart({
	item,
	user,
	animate,
	ultraSize,
}: Omit<BundleCompositionProps, 'items'> & {item: CosmeticItemResponse}) {
	if (isRoomItemType(item.type)) {
		return <CharacterImage item={item} data-flx="shop.shop-item-stage.composition-part.character-image" />;
	}
	const wearer = previewUserFor(user, item);
	switch (item.type) {
		case 'nameplate':
			return (
				<div className={styles.compositionPlate} data-flx="shop.shop-item-stage.composition-plate">
					<ShopMemberRowPreview
						user={wearer}
						animateNameplate={animate}
						data-flx="shop.shop-item-stage.composition-member-row-preview"
					/>
				</div>
			);
		case 'profile_effect':
			return (
				<div className={styles.compositionPanel} data-flx="shop.shop-item-stage.composition-panel">
					<ProfilePreview
						user={wearer}
						previewProfileEffect={previewEffectFor(item)}
						showMembershipInfo={false}
						showMessageButton={false}
						showPreviewLabel={false}
						data-flx="shop.shop-item-stage.composition-profile-preview"
					/>
				</div>
			);
	}
	// Ultra wings at their natural size on the compact surface (a chat avatar, or the showcase's larger one), never
	// scaled: the card makes room for them. They always play, so the shop shows them open.
	if (item.type === 'avatar_ultra') {
		return (
			<div className={styles.compositionUltra} data-flx="shop.shop-item-stage.composition-ultra">
				<Avatar
					user={wearer}
					size={ultraSize}
					decorationSurface="compact"
					forceAnimate
					data-flx="shop.shop-item-stage.composition-ultra-image"
				/>
			</div>
		);
	}
	// An avatar decoration, on the avatar.
	return (
		<div className={styles.compositionAvatar} data-flx="shop.shop-item-stage.composition-avatar">
			<Avatar
				user={wearer}
				size={80}
				decorationSurface="profile"
				forceAnimate={animate}
				data-flx="shop.shop-item-stage.composition-avatar-image"
			/>
		</div>
	);
}

// A faint row above and below the user's own: no made-up people in the member list.
function SkeletonRow() {
	return (
		<div className={styles.skeletonRow} data-flx="shop.shop-item-stage.skeleton-row">
			<span className={styles.skeletonAvatar} data-flx="shop.shop-item-stage.skeleton-avatar" />
			<span className={styles.skeletonName} data-flx="shop.shop-item-stage.skeleton-name" />
		</div>
	);
}
